import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { ChevronDown } from 'lucide-react';
import { appPath } from '@featit/shared/addressing/paths';
import { SHELL_WORK_AREA_EDIT_PATH } from '@featit/shared/shell-paths';
import type { ChromeTranslateFn } from '@featit/shared';
import { chromeLabelText } from '@featit/shared/chrome-label';
import type { IAddressFrame } from '@/routing/workAreaRouteState';
import { englishT, useT } from '@/i18n/LocaleProvider';
import { hasShellCapability, shellDrawsNavigation } from './shellDetection';
import { usePhoneNavigation } from './usePhoneNavigation';
import { backLabelOfAddress } from './historyBackLabel';
import {
  drawerRootOf,
  hasRouteParent,
  phoneTabRootOf,
  routeParentOf,
  type IRouteParent,
  type PhoneTabRootType,
} from './routeParents';

/**
 * I-2453: the part of the phone chrome the app shell reads on every screen — which rows a screen
 * draws, the sub-page a drawer root has open, `data-action` and the scope line's ▾ — apart from the
 * rows themselves (`phoneChrome.tsx`), which only phone mode draws. The shell imports this, so the
 * rows stay out of the entry chunk every first screen loads; `phoneChrome.tsx` re-exports all of it.
 */

/** The attribute every phone control names its registry entry with (*Every action and its rule*). */
export const PHONE_ACTION_ATTRIBUTE = 'data-action';

/**
 * `data-action` for a control. An id the registry does not hold is a programming error, caught by
 * `phoneChrome.test.tsx` and by the e2e audit; it is still written, so the audit can name it.
 */
export function phoneAction(id: string): { 'data-action': string } {
  // In development only, and loaded then: a static import put the whole registry in the launch
  // entry, though production never reads it here (T-0770, the entry budget).
  if (import.meta.env.DEV) {
    void import('@featit/shared/phone-actions').then(({ phoneActionOf }) => {
      if (!phoneActionOf(id)) {
        console.error(
          `[phone-actions] "${id}" is not in the registry (shared/src/phone-actions.ts)`,
        );
      }
    });
  }
  return { [PHONE_ACTION_ATTRIBUTE]: id } as { 'data-action': string };
}

/** Which rows the screen at `pathname` draws in phone mode. */
export interface IPhoneChromeRows {
  /** The tab root whose top row draws (every tab root, Agents too since I-1354), or `null`. */
  readonly topRow: PhoneTabRootType | null;
  /** A tab root, top row or not: its heading is hidden and ＋ floats. */
  readonly tabRoot: PhoneTabRootType | null;
  /** The pushed screen's parent, when the back row draws. */
  readonly backRow: IRouteParent | null;
  /** A drawer-root screen (Settings, the Parts tree…): its title, when the page top row draws. */
  readonly pageRoot: { readonly title: string; readonly path: string } | null;
}

const NO_ROWS: IPhoneChromeRows = { topRow: null, tabRoot: null, backRow: null, pageRoot: null };

/**
 * The rule, for a caller that already knows phone mode (a test, the e2e audit's twin), with the
 * rows' names in the reader's language (`t`, T-0838).
 */
export function phoneChromeRowsIn(
  t: ChromeTranslateFn,
  pathname: string,
  phoneMode: boolean,
  pageTitle?: string | null,
): IPhoneChromeRows {
  if (!phoneMode) return NO_ROWS;
  // F-0530 (AC-19.4): in the app the work-area edit view sits under its own native bar, title and
  // Done; a web row there would be a second header with a second way out.
  if (pathname === SHELL_WORK_AREA_EDIT_PATH && shellDrawsNavigation()) return NO_ROWS;
  const tabRoot = phoneTabRootOf(pathname);
  const parent = tabRoot ? null : routeParentOf(pathname, t, pageTitle);
  // The native bar keeps its own back until the shell holds `webChrome` (the handshake, S-4).
  const backRowAllowed = !shellDrawsNavigation() || hasShellCapability('webChrome');
  return {
    tabRoot,
    topRow: tabRoot,
    backRow: parent && backRowAllowed ? parent : null,
    pageRoot: backRowAllowed ? drawerRootOf(pathname, t) : null,
  };
}

/**
 * ⚠️ `phoneChromeRowsIn` in English, for `SettingsRail.tsx`, which reads only whether a row is drawn
 * (T-0838). It goes when that page passes its own `t`; nothing that draws a row's name calls it.
 */
export function phoneChromeRowsOf(
  pathname: string,
  phoneMode: boolean,
  pageTitle?: string | null,
): IPhoneChromeRows {
  return phoneChromeRowsIn(englishT, pathname, phoneMode, pageTitle);
}

/**
 * U-0009 (AC-18.13a): where a dead address's Back goes with nothing behind it — the nearest address
 * above it that is a tab's root (`/-/work-areas/decisions/D-0157` → Decisions), else `fallback`
 * (the work area's Items, or Items). Only an app address is walked: above a work area's dead
 * address may be a work area that is not there either, and `fallback` already names the one that is.
 */
export function addressErrorWayBackOf(pathname: string, fallback: string): string {
  if (!pathname.startsWith(`${appPath('/')}/`)) return fallback;
  const segments = pathname.split('/').filter(Boolean);
  for (let length = segments.length - 1; length > 0; length -= 1) {
    const above = `/${segments.slice(0, length).join('/')}`;
    if (phoneTabRootOf(above)) return above;
  }
  return fallback;
}

/**
 * I-1265 (AC-18.13, U-0009): an address that leads nowhere (`NoPageHere`, no work area at it) is
 * none of the route table's kinds, so it drew no row — and in the app, with the native bar hidden,
 * no way back at all. It draws the back row: a pop with in-app history, otherwise a replace to
 * `wayBack` (`addressErrorWayBackOf`), named as IL-18 names any page. Its page keeps "Go to work
 * items" as the way on. I-2439 (IN-107): the row names the frame by its label, in the reader's
 * language (_Hier ist keine Seite_), never by the English `title` the frame logic reads.
 */
export function addressErrorRowsOf(
  t: ChromeTranslateFn,
  frame: Pick<IAddressFrame, 'title' | 'label'>,
  wayBack: string,
  phoneMode: boolean,
): IPhoneChromeRows {
  if (!phoneMode) return NO_ROWS;
  const backRowAllowed = !shellDrawsNavigation() || hasShellCapability('webChrome');
  if (!backRowAllowed) return NO_ROWS;
  return {
    ...NO_ROWS,
    backRow: {
      path: wayBack,
      label: backLabelOfAddress(wayBack, t),
      recordKey: frame.label ? chromeLabelText(frame.label, t) : (frame.title ?? ''),
      recordType: 'page',
    },
  };
}

/** `pageTitle`: the screen's own name where only the page knows it (`barTitleOf`'s option). */
export function usePhoneChromeRows(pathname: string, pageTitle?: string | null): IPhoneChromeRows {
  const t = useT();
  return phoneChromeRowsIn(t, pathname, usePhoneNavigation(), pageTitle);
}

/**
 * Whether the chrome draws this screen's way back — the web's back row, or the shell's own
 * navigation, which draws a native back (and owns the edge swipe) whether or not it has handed the
 * web `webChrome`. ⛔ One rule for every page that would otherwise draw a back control of its own
 * (the item page's "Work items", a stacked decision's back link, the decision form's): where this is
 * true, the page draws none. Never two back controls on one screen.
 */
export function chromeDrawsBackOf(pathname: string, phoneMode: boolean): boolean {
  if (!phoneMode) return false;
  // The back row's rule (`phoneChromeRowsIn`), whatever its names say.
  return shellDrawsNavigation() || (phoneTabRootOf(pathname) === null && hasRouteParent(pathname));
}

export function useChromeDrawsBack(pathname: string): boolean {
  return chromeDrawsBackOf(pathname, usePhoneNavigation());
}

/**
 * Whether this screen is a tab root in phone mode: its title is the hidden heading, it draws no
 * description and no in-content create, and its create is the floating ＋.
 */
export function usePhoneTabRoot(pathname: string): boolean {
  return usePhoneChromeRows(pathname).tabRoot !== null;
}

// ── A drawer root's open sub-page (Settings → Appearance) ───────────────────────────────────────

/**
 * A drawer-root page can open a sub-page of its own without a route change (Settings' sections are
 * `?section=`). While one is open the page top row says where it is — ‹ the page's name, and the
 * sub-page's title — so the row stays the one way back, and the page draws no second back link.
 * A tab root's sub-page (Agents' open session, `?session=`, F-0589) draws its ‹ in the top row,
 * before the scope.
 */
export interface IPhoneSubPage {
  readonly title: string;
  readonly backLabel: string;
  readonly onBack: () => void;
}

let subPage: IPhoneSubPage | null = null;
const subPageListeners = new Set<() => void>();

function subscribeSubPage(listener: () => void): () => void {
  subPageListeners.add(listener);
  return () => subPageListeners.delete(listener);
}

/** The open sub-page, for the rows that draw it (`TopRow`, `PageTopRow`). */
export function useOpenPhoneSubPage(): IPhoneSubPage | null {
  return useSyncExternalStore(subscribeSubPage, () => subPage);
}

/** F-0530 (AC-19.2): whether a drawer root has a sub-page open, so its ‹ back row draws. */
export function usePhoneSubPageOpen(): boolean {
  return useSyncExternalStore(
    subscribeSubPage,
    () => subPage !== null,
    () => false,
  );
}

/** Registers `sub` for as long as it is non-null and the caller is mounted. */
export function usePhoneSubPage(sub: IPhoneSubPage | null): void {
  const title = sub?.title ?? null;
  const backLabel = sub?.backLabel ?? null;
  const onBack = useRef(sub?.onBack);
  onBack.current = sub?.onBack;
  useLayoutEffect(() => {
    if (title === null || backLabel === null) return;
    subPage = { title, backLabel, onBack: () => onBack.current?.() };
    for (const listener of subPageListeners) listener();
    return () => {
      subPage = null;
      for (const listener of subPageListeners) listener();
    };
  }, [title, backLabel]);
}

/**
 * AC-19.17 (I-1368): the ▾ of every compact scope line — the app's `AppScopeLine` and the top row's
 * scope a browser or the iPad app's compact window draws — so the two lines are one look. The top
 * row drew a text "▾", a filled triangle, beside the app line's quiet chevron.
 */
export function ScopeLineChevron({ held = false }: { held?: boolean } = {}) {
  return (
    <ChevronDown
      aria-hidden="true"
      data-scope-chevron=""
      // I-1423: held, it keeps its room but is not drawn, until the name it follows is the confirmed one.
      className={`h-3.5 w-3.5 shrink-0 text-[var(--evx-muted)]${held ? ' invisible' : ''}`}
      strokeWidth={2.25}
    />
  );
}
